import React, { useEffect, useMemo, useState } from "react";
import {
  getTrueTodoKpiSummaryOnce,
  subscribeTrueTodoKpiSummary,
  triggerRebuildTrueTodoKpiSummaryNow,
  TrueTodoKpiSummary,
} from "../../services/trueTodoKpiSummaryService";
import { seedDemoDataBNow } from "../../services/demoSeedBService";

export function DashboardTodosLiveDebugTab(_props?: any) {
  const [summary, setSummary] = useState<TrueTodoKpiSummary | null>(null);
  const [status, setStatus] = useState<"connecting" | "connected" | "error">("connecting");
  const [msg, setMsg] = useState<string>("初始化...");
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const updatedAtText = useMemo(() => {
    if (!summary?.updatedAt) return "";
    try {
      if (typeof (summary.updatedAt as any).toDate === "function") {
        return (summary.updatedAt as any).toDate().toLocaleString();
      }
      if (typeof summary.updatedAt === "string") return summary.updatedAt;
    } catch {
      // ignore
    }
    return "";
  }, [summary?.updatedAt]);

  useEffect(() => {
    setStatus("connecting");
    setMsg("監聽 dashboard_daily_summary/trueTodoLive ...");

    const unsub = subscribeTrueTodoKpiSummary(
      (kpi) => {
        setSummary(kpi);
        setStatus("connected");
        setMsg(kpi ? "已接收 snapshot" : "Doc 不存在（可按下重建）");
      },
      (err) => {
        setStatus("error");
        setMsg(err?.message || "監聽失敗");
      }
    );

    return () => {
      try {
        unsub?.();
      } catch {
        // ignore
      }
    };
  }, []);

  const reloadOnce = async () => {
    setBusy(true);
    try {
      setStatus("connecting");
      setMsg("getDoc 讀取中...");
      const kpi = await getTrueTodoKpiSummaryOnce();
      setSummary(kpi);
      setStatus("connected");
      setMsg(kpi ? "getDoc 成功" : "Doc 不存在（可按下重建）");
    } catch (err: any) {
      setStatus("error");
      setMsg(err?.message || "getDoc 失敗");
    } finally {
      setBusy(false);
    }
  };

  const rebuildNow = async () => {
    setBusy(true);
    try {
      setStatus("connecting");
      setMsg("呼叫 rebuildTrueTodoKpiSummaryNow ...");
      const res = await triggerRebuildTrueTodoKpiSummaryNow();
      await reloadOnce();
      if (res?.countedFlows != null) {
        setMsg(`重建完成（counted=${res.countedFlows}, processed=${res.processedFlows}）`);
      }
      setStatus("connected");
    } catch (err: any) {
      setStatus("error");
      setMsg(err?.message || "重建失敗");
    } finally {
      setBusy(false);
    }
  };

  const seedDemoNow = async () => {
    if (busy) return;
    setBusy(true);
    setSeeding(true);
    try {
      setStatus("connecting");
      setMsg("建立/覆蓋 Demo 資料（模式 B：每功能五筆）...");
      const res = await seedDemoDataBNow();
      // seed function 本身會重建 KPI；這裡再讀一次確保畫面同步
      await reloadOnce();
      if (res?.seededDocs != null) {
        setMsg(`Demo 建立完成（寫入/覆蓋 ${res.seededDocs} 筆），KPI 已同步更新`);
      } else {
        setMsg("Demo 建立完成，KPI 已同步更新");
      }
      setStatus("connected");
    } catch (err: any) {
      setStatus("error");
      setMsg(err?.message || "Demo 建立失敗");
    } finally {
      setBusy(false);
      setSeeding(false);
    }
  };

  const statusColor = status === "connected" ? "#10b981" : status === "error" ? "#ef4444" : "#fbbf24";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, height: "100%", paddingBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h3 className="h1" style={{ color: "#60a5fa", margin: 0 }}>
          Live KPI Summary (備案2)
        </h3>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <span
            style={{
              fontSize: 12,
              padding: "4px 8px",
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${statusColor}`,
              color: statusColor,
            }}
          >
            {status.toUpperCase()}
          </span>

          <button className="btn" style={{ width: "auto" }} onClick={reloadOnce} disabled={busy}>
            🔄 getDoc
          </button>

          <button className="btn" style={{ width: "auto", background: "#f59e0b", color: "#000" }} onClick={rebuildNow} disabled={busy}>
            🧱 重建
          </button>

          <button
            className="btn"
            style={{ width: "auto", background: "#10b981", color: "#000" }}
            onClick={seedDemoNow}
            disabled={busy}
            title="（超管）建立/覆蓋 Demo 資料（模式 B：每功能五筆），並同步更新 KPI"
          >
            {seeding ? "🌱 建立中..." : "🌱 建立 Demo(B)"}
          </button>
        </div>
      </div>

      <div style={{ color: status === "error" ? "#fca5a5" : "#9ca3af", fontSize: 12 }}>
        {msg} {updatedAtText ? `（updatedAt: ${updatedAtText}）` : ""}
      </div>

      <div style={{ background: "#0b1220", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: 12 }}>
        <div style={{ fontSize: 12, color: "#93c5fd", marginBottom: 8 }}>Raw JSON</div>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#e5e7eb", fontSize: 12, lineHeight: 1.4 }}>
          {JSON.stringify(summary, null, 2)}
        </pre>
      </div>
    </div>
  );
}
